/* ──────────────────────────────────────────────────────────────
   QuotaContext.tsx · per-plan usage/quota from the meter
   Fetches GET /meter/usage once (and on demand) and exposes it to the shell:
   quota bars on the dashboard + at-cap gating of metered actions (e.g. the
   "New item" button). Plan-generic — reads used/cap/blocked, never trial-only.
   ────────────────────────────────────────────────────────────── */

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { getMeterUsage } from '../services/catalog/meter';
import { useSession } from './sessionContext';
import type { MeterMetricUsage, MeterUsageResponse } from '../schemas/catalog/meter';

interface QuotaValue {
  /** Raw usage payload (enabled/degraded/metrics). */
  usage: MeterUsageResponse;
  loading: boolean;
  /** Usage for one metric, or undefined when metering is off / not reported. */
  byMetric: (metric: string) => MeterMetricUsage | undefined;
  /** true only when that metric is actively at/over its cap (blocked). */
  isBlocked: (metric: string) => boolean;
  /** Re-read usage (call after create/delete or on screen focus). */
  refresh: () => Promise<void>;
}

const EMPTY: MeterUsageResponse = { enabled: false, degraded: false, metrics: [] };

const QuotaContext = createContext<QuotaValue>({
  usage: EMPTY,
  loading: true,
  byMetric: () => undefined,
  isBlocked: () => false,
  refresh: async () => {},
});

export function QuotaProvider({ children }: { children: React.ReactNode }) {
  // Quota is a signed-in concern. Read the platform session so we NEVER hit
  // GET /meter/usage before authentication — an anonymous call 401s in prod
  // (the dev-header fallback is off there) and just adds noise to the logs.
  const { user, loading: sessionLoading } = useSession();

  const [usage, setUsage] = useState<MeterUsageResponse>(EMPTY);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      setUsage(await getMeterUsage());
    } finally {
      setLoading(false);
    }
  }, []);

  // Only fetch usage once the session probe has resolved AND a user exists.
  // Logged out → nothing to load, so clear loading instead of hanging.
  useEffect(() => {
    if (sessionLoading) return;
    if (user) {
      refresh();
    } else {
      setLoading(false);
    }
  }, [sessionLoading, user, refresh]);

  const byMetric = useCallback(
    (metric: string) => usage.metrics.find((m) => m.metric === metric),
    [usage],
  );

  // Gate only on a genuine block. If metering is off/degraded or the metric
  // isn't reported, never block the user (fail-open, mirroring the backend).
  const isBlocked = useCallback(
    (metric: string) => (usage.enabled && !usage.degraded ? byMetric(metric)?.blocked === true : false),
    [usage, byMetric],
  );

  return (
    <QuotaContext.Provider value={{ usage, loading, byMetric, isBlocked, refresh }}>
      {children}
    </QuotaContext.Provider>
  );
}

export const useQuota = () => useContext(QuotaContext);
